import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Star, TrendingUp } from "lucide-react";
import { LaptopMockup, PhoneMockup, FloatingChip } from "@/components/Mockups";
import { useAnchorNav } from "@/lib/nav";

const ease = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const go = useAnchorNav();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMock = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yChips = useTransform(scrollYProgress, [0, 1], [0, 150]);

  return (
    <section id="hem" ref={ref} className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pb-28 lg:pt-40">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,236,209,0.05) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      <div aria-hidden="true" className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border-[60px] border-navy2" />
      <div aria-hidden="true" className="absolute -left-52 bottom-0 h-[380px] w-[380px] rounded-full border border-teal/30" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="flex items-center gap-2.5 font-display text-xs font-bold uppercase tracking-[0.22em] text-orange"
          >
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-orange" />
            Svensk digitalbyrå
          </motion.p>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease }}
              >
                Digitalt som driver
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.32, ease }}
              >
                företag <span className="text-orange">framåt.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-cream/70 sm:text-lg"
          >
            Vi hjälper företag att synas bättre online, bygga förtroende hos sina kunder och skapa en professionell
            digital närvaro.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#kontakt"
              data-testid="hero-cta-primary"
              onClick={(e) => go(e, "kontakt")}
              className="inline-flex items-center gap-2 rounded-lg bg-orange px-7 py-4 font-display text-base font-bold text-navy shadow-[0_4px_14px_rgba(255,125,0,0.3)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange2"
            >
              Boka kostnadsfritt möte <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a
              href="#tjanster"
              data-testid="hero-cta-secondary"
              onClick={(e) => go(e, "tjanster")}
              className="inline-flex items-center gap-2 rounded-lg bg-teal px-7 py-4 font-display text-base font-bold text-white shadow-[0_4px_14px_rgba(21,97,109,0.35)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-slate2"
            >
              Se våra tjänster
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {["Fasta priser", "Personlig service", "Kostnadsfritt första möte"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm font-medium text-cream/60">
                <Check size={16} className="text-teal" aria-hidden="true" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease }}
          className="relative"
        >
          <motion.div style={{ y: yMock }} className="relative">
            <LaptopMockup />
            <div className="absolute -bottom-10 -left-4 sm:-left-10">
              <PhoneMockup />
            </div>
          </motion.div>
          <motion.div style={{ y: yChips }} className="pointer-events-none absolute inset-0">
            <FloatingChip
              icon={Star}
              title="Google Företagsprofil"
              sub="Bättre synlighet lokalt"
              className="absolute -right-2 -top-8 sm:-right-6"
            />
            <FloatingChip
              icon={TrendingUp}
              title="Fler förfrågningar"
              sub="Webb, design & annonsering"
              delay={1.2}
              className="absolute -bottom-6 right-2 sm:right-8"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
